import React, { useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";
import Typography from "@mui/material/Typography";
import withStyles from "@mui/styles/withStyles";
import { FormControl, FormControlLabel, FormLabel, Grid } from "@mui/material";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Eye, EyeClosed, Translate } from "@phosphor-icons/react";
import { Alert, Link } from "@mui/material";

import ANLogoSrc from "assets/an_purple_gray.png";
import SplashScreen from "../AcceptInvitation/SplashScreen";
import styles from "../Signin/styles";
import { PasswordValidationMarkers } from "./PasswordValidationMarkers";
import validationSchema from "./validationSchema";
import i18n, { selectLanguage } from "../../i18n";

const year = new Date().getFullYear();
const APP_VERSION = process.env.APP_VERSION || "";

const ResetPassword = (props) => {
  const { t } = useTranslation(["auth", "common"]);
  const {
    classes,
    username,
    currentPassword,
    verificationCode,
    resetPasswordError,
    resetPasswordErrorMessage,
    resetPasswordErrorCode,
    onChange,
    onSubmit,
    change,
    invitationSignin,
    onSubmitInvitationLogin,
    invitationError,
    invitationCompletionError,
    loginInvitation,
    showVersion = true,
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const selectedLanguage = i18n.resolvedLanguage === "es" ? "es" : "en-US";

  const {
    values,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
    touched,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
      // Initialize currentPassword only if it's a change password
      showCurrentPassword: (change && !invitationSignin) || false,
      currentPassword: currentPassword || "",
      // Initialize agreeToTermsAndPrivacy only if it's an invitation signin
      showAgreeToTerms: invitationSignin || false,
      agreeToTermsAndPrivacy: false,
    },
    validationSchema,
    onSubmit: (values) => {
      if (invitationSignin) {
        onSubmitInvitationLogin(values).then(() => {
          resetForm();
        });
      } else {
        onSubmit(values);
      }
    },
  });

  const buttonDisabled =
    invitationSignin && loginInvitation
      ? !loginInvitation.email || invitationError
      : change
        ? !username || !verificationCode || !currentPassword
        : !username || !verificationCode;

  const handleShowPassword = () => {
    setShowPassword((prevStatus) => !prevStatus);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword((prevStatus) => !prevStatus);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCurrentPasswordChange = (e) => {
    handleChange(e);
    onChange("currentPassword")(e);
  };

  const openLanguageMenu = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const closeLanguageMenu = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageSelection = (language) => {
    selectLanguage(language);
    closeLanguageMenu();
  };

  const showSplashScreen =
    invitationError || (resetPasswordError && resetPasswordErrorCode === "ExpiredCodeException");

  if (showSplashScreen) {
    return (
      <SplashScreen
        title={t("auth:resetPassword.linkExpiredTitle")}
        description={
          <>
            {t("auth:resetPassword.linkExpiredDescription")} <br />
            (800) 486-9060.
          </>
        }
        alertSeverity="error"
      />
    );
  }

  return (
    <Grid container justifyContent="center" alignContent="space-between">
      <Grid item className={classes.resetPasswordFormContainer} marginTop="60px">
        <img src={ANLogoSrc} alt="logo purple and gray" width={198} height={48} />

        <div className={classes.signinForm}>
          <form className={classes.form} onSubmit={handleSubmit}>
            <Typography component="h1" variant="h2" align="center">
              {change
                ? invitationSignin
                  ? t("auth:resetPassword.createAccountTitle")
                  : t("auth:resetPassword.changePasswordTitle")
                : t("auth:resetPassword.resetPasswordTitle")}
            </Typography>

            {change && !invitationSignin && (
              <FormControl fullWidth sx={{ marginTop: 7 }}>
                <FormLabel
                  component="legend"
                  style={{
                    marginBottom: 10,
                    fontSize: 16,
                    fontWeight: 500,
                    color: "#2A2D39",
                  }}
                >
                  {t("auth:resetPassword.currentPasswordLabel")}
                </FormLabel>
                <TextField
                  name="currentPassword"
                  type="password"
                  id="currentPassword"
                  autoComplete="off"
                  value={values.currentPassword}
                  error={touched.currentPassword && Boolean(errors.currentPassword)}
                  onChange={handleCurrentPasswordChange}
                  onBlur={handleBlur}
                  placeholder={t("auth:resetPassword.currentPasswordPlaceholder")}
                />
              </FormControl>
            )}

            <FormControl fullWidth sx={{ marginTop: 7 }}>
              <FormLabel
                component="legend"
                style={{
                  fontSize: 12,
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "100%",
                  letterSpacing: "0.3px",
                  marginBottom: 8,
                }}
              >
                {t("auth:resetPassword.newPasswordLabel")}
              </FormLabel>

              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder={t("auth:resetPassword.newPasswordPlaceholder")}
                autoComplete="off"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("auth:resetPassword.togglePasswordVisibility")}
                        onClick={handleShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        size="small"
                      >
                        {showPassword ? <Eye /> : <EyeClosed />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ marginTop: 7 }}>
              <FormLabel
                component="legend"
                style={{
                  fontSize: 12,
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "100%",
                  letterSpacing: "0.3px",
                  marginBottom: 8,
                }}
              >
                {t("auth:resetPassword.confirmPasswordLabel")}
              </FormLabel>
              <TextField
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                placeholder={t("auth:resetPassword.confirmPasswordPlaceholder")}
                autoComplete="off"
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("auth:resetPassword.toggleConfirmPasswordVisibility")}
                        onClick={handleShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        size="small"
                      >
                        {showConfirmPassword ? <Eye /> : <EyeClosed />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <PasswordValidationMarkers
              classes={classes}
              password={values.password}
              confirmPassword={values.confirmPassword}
            />

            {invitationSignin && (
              <FormControl sx={{ marginTop: 7 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreeToTermsAndPrivacy"
                      checked={values.agreeToTermsAndPrivacy}
                      onClick={handleChange}
                      onBlur={handleBlur}
                    />
                  }
                  label={
                    <Typography component="p" className={classes.agreeToTermsPrivacyText}>
                      {t("auth:resetPassword.agreeToTermsPrefix")}{" "}
                      <Link
                        target="_blank"
                        href="https://www.getanswersnow.com/terms"
                        rel="noreferrer"
                        underline="none"
                        color={"info.main"}
                      >
                        {t("auth:resetPassword.termsOfUse")}
                      </Link>{" "}
                      {t("auth:resetPassword.agreeToTermsMiddle")}
                      <Link
                        href="https://www.getanswersnow.com/privacy"
                        target="_blank"
                        rel="noreferrer"
                        underline="none"
                        color={"info.main"}
                      >
                        {" "}
                        {t("auth:resetPassword.privacyPolicy")}
                      </Link>
                    </Typography>
                  }
                />
              </FormControl>
            )}

            <div className={classes.buttonContainer}>
              <LoadingButton
                type="submit"
                loading={isSubmitting}
                disabled={!isValid || !dirty || buttonDisabled}
                sx={{ height: 42, width: "100%", marginTop: 7 }}
                color="primary"
              >
                {change
                  ? invitationSignin
                    ? t("auth:resetPassword.createPasswordButton")
                    : t("auth:resetPassword.changePasswordButton")
                  : t("auth:resetPassword.resetPasswordButton")}
              </LoadingButton>
            </div>

            {invitationSignin && (
              <Typography variant="caption" component="p" textAlign="center" sx={{ marginTop: 7 }}>
                {t("auth:resetPassword.invitationDisclaimer")}
              </Typography>
            )}

            {resetPasswordError && (
              <div style={{ marginTop: 15, marginBottom: 15 }}>
                {resetPasswordErrorMessage &&
                resetPasswordErrorMessage !== "Incorrect username or password." ? (
                  <Alert severity="error">{resetPasswordErrorMessage}</Alert>
                ) : (
                  <Alert severity="error">{t("auth:resetPassword.genericError")}</Alert>
                )}
              </div>
            )}

            {invitationSignin && invitationCompletionError && (
              <div style={{ marginTop: 15, marginBottom: 15 }}>
                <Alert severity="error">{invitationCompletionError}</Alert>
              </div>
            )}

            <Button
              aria-controls={languageMenuAnchor ? "language-menu" : undefined}
              aria-expanded={languageMenuAnchor ? "true" : undefined}
              aria-haspopup="menu"
              className={classes.languageButton}
              color="secondary"
              onClick={openLanguageMenu}
              startIcon={<Translate size={22} weight="bold" />}
              variant="text"
            >
              {t(`common:languages.${selectedLanguage}`)}
            </Button>
            <Menu
              anchorEl={languageMenuAnchor}
              anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
              id="language-menu"
              onClose={closeLanguageMenu}
              open={Boolean(languageMenuAnchor)}
              transformOrigin={{ horizontal: "center", vertical: "top" }}
            >
              <MenuItem
                onClick={() => handleLanguageSelection("en-US")}
                selected={selectedLanguage === "en-US"}
              >
                {t("common:languages.en-US")}
              </MenuItem>
              <MenuItem
                onClick={() => handleLanguageSelection("es")}
                selected={selectedLanguage === "es"}
              >
                {t("common:languages.es")}
              </MenuItem>
            </Menu>
          </form>
        </div>
      </Grid>

      <Grid
        item
        xs={12}
        component="footer"
        textAlign="center"
        color="text.secondary"
        sx={{ marginTop: 7 }}
      >
        {showVersion && (
          <div>
            <Typography component="p" className={classes.footerText}>
              {t("auth:resetPassword.copyright", { version: APP_VERSION, year })}
            </Typography>
          </div>
        )}

        {invitationSignin && (
          <>
            <Typography component="p" variant="body2">
              {t("auth:resetPassword.questionsPrompt")}
            </Typography>
            <Typography component="span" variant="body2" color="primary">
              804-215-5600
            </Typography>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default withRouter(withStyles(styles)(ResetPassword));
